import { useEffect } from "react";
import { attachRecoveryStream } from "../../utils/streamingUtils";
import useStreamingStateStore from "../../store/useStreamingStateStore";
import useGenerateApiSignal from "../../store/useGenerateApiSignal";
import QUERYKEYS from "../../api/queryKeys";

/**
 * Reattaches to any generation that was still running when the user navigated away.
 *
 * pendingGenerations is keyed by messageId and survives detachStreaming() so we
 * can find the in-progress entry by scanning the current messages list.
 * Runs once per chat when messages first load (isDataReady becomes true).
 *
 * If the SSE stream drops before delivering a terminal event, attachRecoveryStream
 * falls back to polling the status endpoint with exponential backoff.
 */
export function useReconnectStream({
  chat_id,
  space_id,
  messages,
  isDataReady,
  apiUrl,
  queryClient,
  setIsLatestMessageLoading,
  setGenerateStatus,
}) {
  const messageIds = messages.map((message) => message.id).join('|');

  useEffect(() => {
    if (!isDataReady || !messages.length || !chat_id) return;

    const { pendingGenerations } = useStreamingStateStore.getState();
    if (!Object.keys(pendingGenerations).length) return;

    for (const msg of messages) {
      const messageId = msg.id;
      const pending = pendingGenerations[messageId];
      if (!pending) continue;

      // Skip if we are already streaming this message (live stream is active).
      if (useStreamingStateStore.getState().isStreaming(messageId)) continue;

      const { generation_id, latest_seq } = pending;

      // Immediately refresh messages so any content written while the user was
      // away is visible right away — covers completed, failed, and cancelled
      // generations that finished before the user returned.
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chat_id, space_id] });

      // Populate generate signal immediately so the sidebar breathing dot
      // appears as soon as the user lands on this chat.
      useGenerateApiSignal.getState().addSignal(chat_id, {
        generate_id: messageId,
        generation_id,
      });

      const cleanup = attachRecoveryStream({
        generationId: generation_id,
        messageId,
        afterSeq: latest_seq,
        apiUrl,
        queryClient,
        chat_id,
        space_id,
        setIsLatestMessageLoading,
        setGenerateStatus,
      });

      // Only one pending generation per chat at a time.
      return cleanup;
    }
  // Message IDs change when the current chat query resolves after navigation.
  // Content-only updates must not abort an active recovery stream.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat_id, isDataReady, messageIds]);
}
